/**
 * Thin client for the @agencylens/worker-license endpoints.
 */

import { config } from "./config.js";
import type { TeamMemberSummary } from "@agencylens/shared";

export interface StartResponse {
  ok: true;
  sent: boolean;
  devMagicLink?: string;
  warning?: string;
}

export interface ApiError {
  error: string;
  hint?: string;
  email?: string;
}

export interface MeUser {
  email: string;
  tier: string;
  licenseKey: string;
  createdAt: string;
  lastLoginAt?: string;
  role?: "owner" | "member";
  accountEmail?: string;
}

export interface InviteResponse {
  ok: true;
  sent: boolean;
  email?: string;
  devInviteLink?: string;
  warning?: string;
}

export interface MembersResponse {
  members: TeamMemberSummary[];
}

export interface VerifyResponse {
  jwt: string;
  /** Per-buyer Data Encryption Key (base64). Cache in sessionStorage. */
  dek: string;
  user: MeUser;
}

export interface MeResponse {
  /** Per-buyer DEK, re-delivered so the SPA can rebuild its cache post-refresh. */
  dek: string;
  user: MeUser;
}

async function request<T>(
  path: string,
  method: "GET" | "POST",
  body?: unknown,
  jwt?: string,
): Promise<T> {
  const res = await fetch(`${config.licenseWorkerBase}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    ...(method === "POST" ? { body: JSON.stringify(body ?? {}) } : {}),
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`License worker returned non-JSON ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    const err = data as ApiError;
    const e = new Error(err.error || `HTTP ${res.status}`);
    Object.assign(e, err);
    (e as Error & { status?: number }).status = res.status;
    throw e;
  }
  return data as T;
}

function post<T>(path: string, body: unknown, jwt?: string): Promise<T> {
  return request<T>(path, "POST", body, jwt);
}

/** GET /license/me — returns the user record + re-delivers the DEK. */
export function getMe(jwt: string): Promise<MeResponse> {
  return request<MeResponse>("/license/me", "GET", undefined, jwt);
}

export async function startSignin(
  email: string,
  licenseKey?: string,
): Promise<StartResponse> {
  const body: Record<string, string> = { email };
  if (licenseKey) body["licenseKey"] = licenseKey;
  return post<StartResponse>("/license/start", body);
}

export async function verifyMagicLink(
  token: string,
  licenseKey?: string,
): Promise<VerifyResponse> {
  const body: Record<string, string> = { token };
  if (licenseKey) body["licenseKey"] = licenseKey;
  return post<VerifyResponse>("/license/verify", body);
}

export async function refreshSession(jwt: string): Promise<{ jwt: string }> {
  return post<{ jwt: string }>("/license/refresh", {}, jwt);
}

/* -------------------------------------------------- team (Tier 3 sub-users) */

export function inviteMember(jwt: string, email: string): Promise<InviteResponse> {
  return post<InviteResponse>("/team/invite", { email }, jwt);
}

export function listMembers(jwt: string): Promise<MembersResponse> {
  return request<MembersResponse>("/team/members", "GET", undefined, jwt);
}

export function removeMember(
  jwt: string,
  email: string,
): Promise<{ ok: true; removed: string }> {
  return post<{ ok: true; removed: string }>("/team/remove", { email }, jwt);
}

/** Accept a team invite (token from the emailed link). Establishes a member
 *  session, same shape as verifyMagicLink. */
export function acceptInvite(token: string): Promise<VerifyResponse> {
  return post<VerifyResponse>("/team/accept", { token });
}
